import Box from '@mui/material/Box';
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import AuthGuard from '@/components/auth-guard';
import { ConflictResolutionDialog } from '@/components/editor/ConflictResolutionDialog';
import { EditorHeader } from '@/components/editor/EditorHeader';
import { EditorMainArea } from '@/components/editor/EditorMainArea';
import { EntitiesDrawer } from '@/components/editor/EntitiesDrawer';
import { EntityDetailDialog } from '@/components/editor/EntityDetailDialog';
import { EntityDetailDrawer } from '@/components/editor/EntityDetailDrawer';
import { HistoryDrawer } from '@/components/editor/HistoryDrawer';
import { OutlineDrawer } from '@/components/editor/OutlineDrawer';
import { ScenePropertiesDrawer } from '@/components/editor/ScenePropertiesDrawer';
import { UnsavedChangesDialog } from '@/components/editor/UnsavedChangesDialog';
import { WritersRoomDrawer } from '@/components/editor/WritersRoomDrawer';
import { useCreateChapter } from '@/features/chapters/hooks/use-mutation-hooks';
import { useEditorController } from '@/features/editor/hooks/useEditorController';
import { useWriteScene } from '@/features/scenes/hooks/use-mutation-hooks';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { useEditorData } from '@/hooks/useEditorData';
import { useEditorUI } from '@/hooks/useEditorUI';
import { useFloatingCards } from '@/hooks/useFloatingCards';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { useSubscription } from '@/hooks/useSubscription';
import { calculateWordCount } from '@/lib/text-service';
import { EnrichedEntity, NarrativeSummary, Scene, WriterRoomStatus } from '@/lib/types';

const editorSearchSchema = z.object({
  textId: z.string().optional(),
});

const EMPTY_SCENE_IDS: string[] = [];

export const Route = createFileRoute('/editor')({
  component: EditorPage,
  validateSearch: (search) => editorSearchSchema.parse(search),
});

interface EditorContentProps {
  expandedChapters: Record<string, boolean>;
  setExpandedChapters: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isOutlineDrawerOpen: boolean;
  setIsOutlineDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function EditorContent({
  expandedChapters,
  setExpandedChapters,
  isOutlineDrawerOpen,
  setIsOutlineDrawerOpen,
}: EditorContentProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const editorAreaRef = useRef<HTMLDivElement>(null);
  const { firestore, backend } = useFirebase();

  // ── HOOKS: The New Clean Stack ──────────────────────────────
  const { novel, batchSaveItems, novelDao, updateNarrativeItem, updateNarrativeSummary } = useNovelRepository();

  // 1. Controller handles selection, doc loading, autosave, and CRUD
  const editor = useEditorController({ selectedTextId: search.textId });

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => editor.isDirty,
    enableBeforeUnload: () => editor.isDirty,
    withResolver: true,
  });

  const { hasFeature } = useSubscription();
  const canUseWritersRoom = hasFeature('ai_assistance_allowed');

  const [isSavingOnLeave, setIsSavingOnLeave] = useState(false);
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);

  // 2. UI handles drawers and focus mode (partially synced with controller)
  const ui = useEditorUI({
    initialOutlineOpen: true,
    initialScenePropertiesOpen: !!editor.selectedTextId
  });

  // 3. Memoized activeSceneId looking up direct scene summary or textSummaries
  const activeSceneId = useMemo(() => {
    if (!editor.selectedTextId) return undefined;
    const scenesObj = novel?.summaries?.scenes || {};
    const directScene = Object.values(scenesObj).find(
      s => s.mainContentId === editor.selectedTextId || s.contentVariants?.includes(editor.selectedTextId!) || s.id === editor.selectedTextId
    );
    if (directScene) return directScene.id;

    if (!editor.isLoadingTexts) {
      const summary = editor.textSummaries.find(t => t.id === editor.selectedTextId);
      if (summary?.sceneIds?.[0]) return summary.sceneIds[0];
    }
    return editor.selectedTextId;
  }, [editor.selectedTextId, editor.textSummaries, editor.isLoadingTexts, novel?.summaries?.scenes]);

  const selectedSceneIds = useMemo(() => {
    if (!editor.selectedTextId || editor.isLoadingTexts) return EMPTY_SCENE_IDS;
    return editor.textSummaries.find(t => t.id === editor.selectedTextId)?.sceneIds ?? (activeSceneId ? [activeSceneId] : EMPTY_SCENE_IDS);
  }, [editor.selectedTextId, editor.textSummaries, editor.isLoadingTexts, activeSceneId]);

  // 4. Data handles enrichment of related entities
  const data = useEditorData({
    selectedEntitySummary: ui.selectedEntitySummary,
    activeSceneId
  });

  const cards = useFloatingCards(editorAreaRef);
  const { writeScene, createFullScene, pendingSceneId, isCreatingContent } = useWriteScene();
  const { createChapter } = useCreateChapter();

  // ── AUTO-SYNC URL ──────────────────────────────────────────
  // If the scene's mainContentId changes (e.g. Writer's Room finishes), update URL
  const currentMainId = data.activeScene?.mainContentId;
  const prevMainId = useRef(data.activeScene?.mainContentId);
  if (currentMainId && prevMainId.current && currentMainId !== prevMainId.current) {
    prevMainId.current = currentMainId;
    Promise.resolve().then(() => {
      void navigate({
        to: '/editor',
        search: { textId: currentMainId },
        replace: true,
      });
    });
  } else {
    prevMainId.current = currentMainId;
  }

  // ── COMPUTED ────────────────────────────────────────────────
  const { wordCount, characterCount } = useMemo(() => {
    const words = calculateWordCount(editor.editorContent);
    const chars = editor.editorContent.replace(/<[^>]*>/g, '').length;
    return { wordCount: words, characterCount: chars };
  }, [editor.editorContent]);

  // ── HANDLERS ────────────────────────────────────────────────
  const handleCreateText = async (name: string, chapterId?: string) => {
    // If it's a scene text, we use useWriteScene for full orchestration
    await createFullScene(name, chapterId);
  };

  const handleReorderScenes = async (scenesByChapter: Record<string, NarrativeSummary[]>) => {
    const now = new Date().toISOString();
    const updates = Object.entries(scenesByChapter).flatMap(([chapterId, scenes]) => (
      scenes.map((scene, order) => ({
        ...scene,
        chapterId: chapterId === 'unsorted' ? '' : chapterId,
        order,
        updatedAt: now,
      }))
    ));

    await batchSaveItems('narrative', 'scenes', updates);
    const summaryUpdates = Object.fromEntries(updates.map((scene) => [scene.id, scene]));
    await novelDao.batchUpdateSummaries(novel.ownerId, novel, 'narrative', 'scenes', summaryUpdates);
  };

  const handleSaveAll = async (silent = true) => {
    if (!editor.selectedTextId || !novel.id) return;
    if (!editor.isDirty) return; // Prevent unnecessary writes if nothing changed
    try {
      // Save full prose
      await editor.handleSave(undefined, silent);
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleSaveAndLeave = async () => {
    setIsSavingOnLeave(true);
    try {
      await handleSaveAll();
      proceed?.();
    } catch (error) {
      console.error('Failed to save before leaving:', error);
    } finally {
      setIsSavingOnLeave(false);
    }
  };

  const handleSceneUpdate = async (updates: Partial<Scene>) => {
    if (!firestore || !novel.id || !data.activeScene) return;
    try {
      await updateNarrativeItem('scenes', data.activeScene.id, updates);
      if (updates.name || updates.status) {
        const currentSummary = novel.summaries?.scenes?.[data.activeScene.id] as NarrativeSummary | undefined;
        if (currentSummary) {
          await updateNarrativeSummary('scenes', data.activeScene.id, {
            ...currentSummary,
            name: updates.name || currentSummary.name,
            status: updates.status || currentSummary.status,
          });
        }
      }
    } catch (error) {
      console.error('Failed to update scene:', error);
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handlePreserveConflictAsVariant = async () => {
    if (!editor.hasConflict) return;
    setIsResolvingConflict(true);
    try {
      const scene = data.activeScene;
      const baseName = editor.currentTextDoc?.name || scene?.name || 'Recovered draft';
      const variantId = await editor.preserveConflictAsVariant(scene?.id, baseName);
      if (!variantId) return;

      if (scene) {
        const contentVariants = Array.from(new Set([...(scene.contentVariants || []), variantId]));
        await updateNarrativeItem('scenes', scene.id, { contentVariants });
      }

      // The local draft now exists independently, so clearing the conflicted
      // source document cannot lose it or trigger the navigation blocker.
      editor.reloadRemoteVersion();
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await navigate({ to: '/editor', search: { textId: variantId }, replace: true });
      toast({ title: 'Local draft preserved as a separate variant' });
    } catch (error) {
      console.error('Failed to preserve conflict as variant:', error);
      toast({ title: 'Could not preserve local draft', variant: 'destructive' });
    } finally {
      setIsResolvingConflict(false);
    }
  };

  const handleSetAsMain = async (variantId: string) => {
    if (!novel.id || !data.activeScene) return;
    try {
      const oldMainId = data.activeScene.mainContentId;
      const currentVariants = data.activeScene.contentVariants || [];

      // Move old main to variants, remove new main from variants
      const newVariants = Array.from(new Set([
        oldMainId,
        ...currentVariants.filter(id => id !== variantId)
      ].filter(Boolean))) as string[];

      await updateNarrativeItem('scenes', data.activeScene.id, {
        mainContentId: variantId,
        contentVariants: newVariants
      });

      toast({ title: t('editor.variant_promoted') });
    } catch (error) {
      console.error('Failed to set main variant:', error);
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleVersionSelect = async (contentId: string, dayKey: string, seq: number, historyId: string) => {
    if (!backend || !novel.id || !contentId) return;

    try {
      let currentText = '';
      let previousText = '';

      if (historyId === 'current') {
        // For the LIVE version, fetch the latest document state
        const doc = await backend.content.getContent(novel.ownerId, novel, contentId);
        if (!doc) return;
        currentText = doc.scratchpad;
        // Show as plain text by making both sides equal
        previousText = currentText;
      } else {
        const result = await backend.content.reconstructTextAtPoint(novel.ownerId, novel.id, contentId, dayKey, seq);
        if (!result.success) {
          toast({ title: t('editor.error_reconstructing'), variant: 'destructive' });
          return;
        }
        currentText = result.content;
        previousText = result.previousContent;
      }

      ui.setReconstructedVersionText(currentText);
      ui.setReconstructedPreviousText(previousText);
      ui.setVersionToCompare({ id: contentId, seq, dayKey });
    } catch (error) {
      console.error('Failed to reconstruct version:', error);
    }
  };

  const activeChapter = useMemo(() => {
    if (!data.activeScene || !data.chapters) return null;
    return data.chapters.find(c => c.id === data.activeScene?.chapterId);
  }, [data.activeScene, data.chapters]);

  const handleEntitySelect = (entity: EnrichedEntity | null, event: React.MouseEvent) => {
    ui.setIsEntityDrawerOpen(event.altKey);
    ui.setSelectedEntitySummary(entity);
  };

  const handleEntityClose = () => {
    ui.setIsEntityDrawerOpen(false);
    ui.setSelectedEntitySummary(null);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', overflow: 'hidden', bgcolor: 'background.default', color: 'text.primary' }}>
      <OutlineDrawer
        isOpen={isOutlineDrawerOpen}
        isDesktopVisible={!ui.isFocusMode}
        expandedChapters={expandedChapters}
        setExpandedChapters={setExpandedChapters}
        onClose={() => setIsOutlineDrawerOpen(false)}
        chapters={data.chapters}
        scenes={data.scenes}
        onSceneClick={(s) => writeScene(s)}
        onCreateScene={handleCreateText}
        onCreateChapter={createChapter}
        onReorderScenes={handleReorderScenes}
        selectedTextId={editor.selectedTextId}
        pendingSceneId={pendingSceneId}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
        {!ui.isFocusMode && (
        <EditorHeader
          novelName={activeChapter?.name || ''}
          textTitle={data.activeScene?.name || ''}
          status={((data.activeScene as Scene)?.status || { phase: 'idle' }) as WriterRoomStatus}
          onChangeStatus={(newPhase: string) => handleSceneUpdate({ status: { ...((data.activeScene as Scene)?.status || { phase: 'idle' }), phase: newPhase as WriterRoomStatus['phase'] } })}
          isAutosaving={editor.isAutosaving}
          autosaveEnabled={editor.autosaveEnabled}
          onAutosaveToggle={editor.toggleAutosave}
          onSave={handleSaveAll}
          canSave={!!editor.selectedTextId}
          isHistoryDrawerOpen={ui.isHistoryDrawerOpen}
          isEntitiesDrawerOpen={ui.isEntitiesDrawerOpen}
          isScenePropertiesOpen={ui.isScenePropertiesOpen}
          isOutlineDrawerOpen={isOutlineDrawerOpen}
          isWritersRoomOpen={ui.isWritersRoomOpen}
          onToggleHistoryDrawer={ui.handleToggleHistoryDrawer}
          onToggleEntitiesDrawer={ui.handleToggleEntitiesDrawer}
          onToggleSceneProperties={ui.handleToggleSceneProperties}
          onToggleOutlineDrawer={() => setIsOutlineDrawerOpen((isOpen) => !isOpen)}
          onToggleWritersRoom={ui.handleToggleWritersRoom}
          canUseAutosave={editor.canUseAutosave}
          canUseWritersRoom={canUseWritersRoom}
          isFocusMode={ui.isFocusMode}
          onToggleFocusMode={ui.handleToggleFocusMode}
        />
        )}

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
        <EditorMainArea
          editorAreaRef={editorAreaRef}
          isLoadingText={editor.isLoadingText || isCreatingContent}
          selectedTextId={editor.selectedTextId}
          editorContent={editor.editorContent}
          setEditorContent={editor.setEditorContent}
          onCommentChange={editor.handleCommentChange}
          getPlaceholder={() => !editor.selectedTextId ? t('editor.select_or_create_text') : selectedSceneIds.length === 0 ? t('editor.just_for_fun_write') : t('editor.write_your_story')}
          handleDrop={cards.handleDrop}
          handleDragOver={cards.handleDragOver}
          floatingCards={cards.floatingCards}
          handleCloseCard={cards.handleCloseCard}
          handleCardPositionChange={cards.handleCardPositionChange}
          handleFloatingCardClick={(id, event) => handleEntitySelect(cards.floatingCards.get(id)?.entity || null, event)}
          onCreateText={handleCreateText}
          wordCount={wordCount}
          characterCount={characterCount}
          isAutosaving={editor.isAutosaving}
          autosaveEnabled={editor.autosaveEnabled}
          isFocusMode={ui.isFocusMode}
          onToggleFocusMode={ui.handleToggleFocusMode}
          onSave={(content, silent, skipHistory) => editor.handleSave(content, silent, skipHistory)}
          entityHighlights
          entities={[...data.enrichedCharacters, ...data.enrichedLocations, ...data.enrichedOrganizations]}
        />

        <HistoryDrawer
          key={editor.currentTextDoc?.id}
          isOpen={ui.isHistoryDrawerOpen}
          scene={data.activeScene as Scene | null}
          textDoc={editor.currentTextDoc}
          versionToCompare={ui.versionToCompare}
          onSelectVersion={handleVersionSelect}
          onSetAsMain={handleSetAsMain}
          onCloseVersionComparison={() => ui.setVersionToCompare(null)}
          currentContent={ui.reconstructedVersionText}
          reconstructedVersionText={ui.reconstructedPreviousText}
          onClose={() => ui.setIsHistoryDrawerOpen(false)}
          onSave={handleSaveAll}
        />

        <EntitiesDrawer
          isOpen={ui.isEntitiesDrawerOpen}
          search={ui.drawerSearch}
          onSearchChange={ui.setDrawerSearch}
          characters={data.enrichedCharacters}
          locations={data.enrichedLocations}
          organizations={data.enrichedOrganizations}
          events={data.enrichedEvents}
          arcs={data.enrichedArcs}
          onSelectEntity={handleEntitySelect}
          onClose={() => ui.setIsEntitiesDrawerOpen(false)}
          onEntityDragStart={cards.handleEntityDragStart}
        />

        <ScenePropertiesDrawer
          isOpen={ui.isScenePropertiesOpen}
          onClose={() => ui.setIsScenePropertiesOpen(false)}
          scene={data.activeScene as Scene | null}
          onUpdate={handleSceneUpdate}
          onSelectEntity={handleEntitySelect}
        />

        {canUseWritersRoom && (
          <WritersRoomDrawer
            isOpen={ui.isWritersRoomOpen}
            onClose={() => ui.setIsWritersRoomOpen(false)}
            scene={data.activeScene as Scene | null}
            novelId={novel?.id || ''}
            textId={editor.selectedTextId}
          />
        )}

        <ConflictResolutionDialog
          open={editor.hasConflict}
          isResolving={isResolvingConflict}
          onReloadRemote={editor.reloadRemoteVersion}
          onPreserveVariant={handlePreserveConflictAsVariant}
        />
        <EntityDetailDialog entity={ui.isEntityDrawerOpen ? null : data.selectedEntity} onClose={handleEntityClose} />
        <EntityDetailDrawer entity={ui.isEntityDrawerOpen ? data.selectedEntity : null} onClose={handleEntityClose} />
        <UnsavedChangesDialog
          open={status === 'blocked'}
          isSaving={isSavingOnLeave}
          onStay={() => reset?.()}
          onDiscard={() => {
            proceed?.();
          }}
          onSave={handleSaveAndLeave}
        />
        </Box>
      </Box>
    </Box>
  );
}

export default function EditorPage() {
  const search = Route.useSearch();
  const [expandedChapters, setExpandedChapters] = useState<Record<string, boolean>>({});
  const [isOutlineDrawerOpen, setIsOutlineDrawerOpen] = useState(true);

  return (
    <AuthGuard>
      <EditorContent
        key={search.textId ?? 'new-scene'}
        expandedChapters={expandedChapters}
        setExpandedChapters={setExpandedChapters}
        isOutlineDrawerOpen={isOutlineDrawerOpen}
        setIsOutlineDrawerOpen={setIsOutlineDrawerOpen}
      />
    </AuthGuard>
  );
}
